import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import MainLayout from "../layout/MainLayout";
import { useAlertContext } from "../context/AlertContext";
import {
    addAdaptationShare, archiveRole, assignRole, blockAccess, getAccessLog, getAccessMe, getAccessPeople, getAccessSubject,
    getAdaptationShares, getFlags, getOpenAdaptations, getRoles, removeAdaptationShare, saveAccessOverrides, saveDefaultRole,
    saveFlag, saveRole, setServiceAccount, unassignRole, unblockAccess,
} from "../services/accessApi";
import {
    SCOPE_LABELS, SCOPE_ORDER, actionsFor, cellsToGrants, describeGrants, filterPeople, grantsToCells,
    overrideState, setCell, setOverride,
} from "../utils/accessMatrix";

/**
 * «Доступы» — правка №11 (Чат 5), кнопка во вкладке «Сотрудники». [Claude] 2026-11-02
 * Люди (с ERP и без) → роли и личные исключения; роли — матрица «раздел × действие × область»;
 * включение (флаги пилота); журнал изменений прав. Права проверяет backend.
 */

const TABS = [
    { key: "people", label: "Люди" },
    { key: "roles", label: "Роли" },
    { key: "rollout", label: "Включение" },
    { key: "log", label: "Журнал" },
];

const btn = "rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50";
const btnPrimary = "rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-700 disabled:opacity-50";

function errText(e) {
    return e?.message || String(e);
}

/* ---------- матрица (роль или личные исключения) ---------- */

function MatrixTable({ matrix, renderCell }) {
    const fields = matrix.fields || [];
    const actionKeys = Object.keys(matrix.actions || {});
    return <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                    <th className="px-3 py-2 text-left">Раздел / поле</th>
                    {actionKeys.map((a) => <th key={a} className="px-2 py-2 text-center">{matrix.actions[a]}</th>)}
                </tr>
            </thead>
            {(matrix.groups || []).map((group) => <tbody key={group.name}>
                <tr><td colSpan={actionKeys.length + 1} className="bg-amber-50 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-amber-800">{group.name}</td></tr>
                {group.objects.map((object) => {
                    const allowed = actionsFor(object, fields);
                    return <tr key={object} className="border-t border-slate-100" data-testid={`matrix-row-${object}`}>
                        <td className="px-3 py-1.5 text-slate-800">
                            {matrix.objects[object] || object}
                            {fields.includes(object) ? <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500">поле</span> : null}
                        </td>
                        {actionKeys.map((a) => <td key={a} className="px-1 py-1 text-center">
                            {allowed.includes(a) ? renderCell(object, a) : <span className="text-slate-300">·</span>}
                        </td>)}
                    </tr>;
                })}
            </tbody>)}
        </table>
    </div>;
}

function ScopeSelect({ value, onChange, disabled, testId }) {
    return <select value={value || ""} disabled={disabled} onChange={(e) => onChange(e.target.value)} data-testid={testId}
        className={`w-24 rounded-lg border px-1 py-1 text-xs ${value ? "border-emerald-300 bg-emerald-50 font-semibold text-emerald-800" : "border-slate-200 text-slate-400"}`}>
        <option value="">{SCOPE_LABELS[""]}</option>
        {SCOPE_ORDER.map((s) => <option key={s} value={s}>{SCOPE_LABELS[s]}</option>)}
    </select>;
}

/* ---------- Роли ---------- */

function DefaultRole({ me, roles, reload, showAlert }) {
    const current = me?.default_role || "staff";
    const [value, setValue] = useState(current);
    useEffect(() => { setValue(current); }, [current]);
    const save = async () => {
        try {
            await saveDefaultRole(value);
            showAlert("Роль по умолчанию сохранена", "success");
            await reload();
        } catch (e) {
            showAlert(`Не удалось сохранить: ${errText(e)}`, "error");
        }
    };
    return <div className="mb-5 flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4" data-testid="default-role">
        <div className="min-w-[240px] flex-1">
            <div className="font-semibold text-slate-900">Роль по умолчанию</div>
            <div className="text-xs text-slate-500">Для всех, кому роль не назначена. Роль в ERP на права в платформе не влияет.</div>
        </div>
        <select value={value} disabled={!me?.is_owner} onChange={(e) => setValue(e.target.value)} data-testid="default-role-select"
            className="rounded-xl border border-slate-200 px-3 py-2 text-sm">
            {roles.filter((r) => !r.system).map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
        </select>
        {me?.is_owner
            ? <button type="button" className={btnPrimary} disabled={value === current} onClick={save}>Сохранить</button>
            : <span className="text-xs text-slate-500">меняет только владелец</span>}
    </div>;
}

function RolesTab({ matrix, roles, reload, showAlert }) {
    const [selected, setSelected] = useState(roles[0]?.key || null);
    const role = roles.find((r) => r.key === selected) || null;
    const [draft, setDraft] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (role) setDraft({ key: role.key, name: role.name, description: role.description || "", cells: grantsToCells(role.grants), isNew: false });
    }, [role]);

    const startNew = () => {
        setSelected(null);
        setDraft({ key: "", name: "", description: "", cells: {}, isNew: true });
    };

    const save = async () => {
        if (!draft.name.trim() || (draft.isNew && !/^[a-z0-9_]+$/.test(draft.key))) {
            showAlert("Укажите название и код роли (латиница, цифры, «_»)", "warning");
            return;
        }
        setSaving(true);
        try {
            await saveRole(draft.key, { name: draft.name.trim(), description: draft.description.trim() || null, grants: cellsToGrants(draft.cells) });
            showAlert("Роль сохранена", "success");
            await reload();
            setSelected(draft.key);
        } catch (e) {
            showAlert(`Не удалось сохранить роль: ${errText(e)}`, "error");
        } finally {
            setSaving(false);
        }
    };

    const archive = async () => {
        if (!window.confirm(`Удалить роль «${role.name}»? У людей с этой ролью пропадут её права.`)) return;
        try {
            await archiveRole(role.key);
            showAlert("Роль удалена", "success");
            setSelected(null);
            setDraft(null);
            await reload();
        } catch (e) {
            showAlert(`Не удалось удалить роль: ${errText(e)}`, "error");
        }
    };

    const locked = Boolean(role?.system) && !draft?.isNew;

    return <div className="grid gap-5 lg:grid-cols-[260px_1fr]">
        <aside className="space-y-2">
            {roles.map((r) => <button key={r.key} type="button" onClick={() => setSelected(r.key)} data-testid={`role-item-${r.key}`}
                className={`block w-full rounded-xl border px-3 py-2 text-left text-sm ${selected === r.key ? "border-amber-400 bg-amber-50" : "border-slate-200 bg-white hover:bg-slate-50"}`}>
                <div className="font-semibold text-slate-900">{r.name}</div>
                <div className="text-xs text-slate-500">{r.system ? "все права, нельзя урезать" : `${(r.grants || []).length} прав`}</div>
            </button>)}
            <button type="button" onClick={startNew} className={`${btn} w-full`}>+ Новая роль</button>
        </aside>
        {draft ? <section className="space-y-4">
            <div className="grid gap-3 md:grid-cols-3">
                {draft.isNew ? <input value={draft.key} onChange={(e) => setDraft({ ...draft, key: e.target.value.trim().toLowerCase() })} placeholder="код: logist_senior" className="rounded-xl border border-slate-200 px-3 py-2 text-sm" /> : null}
                <input value={draft.name} disabled={locked} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Название" data-testid="role-name" className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold" />
                <input value={draft.description} disabled={locked} onChange={(e) => setDraft({ ...draft, description: e.target.value })} placeholder="Описание" className="rounded-xl border border-slate-200 px-3 py-2 text-sm md:col-span-2" />
            </div>
            {locked ? <p className="rounded-xl bg-slate-100 px-3 py-2 text-sm text-slate-600">Владелец видит и может всё, включая новые разделы. Эту роль нельзя урезать или удалить — так никто не потеряет доступ.</p> : null}
            <MatrixTable matrix={matrix} renderCell={(o, a) => <ScopeSelect testId={`cell-${o}-${a}`} disabled={locked}
                value={locked ? "all" : draft.cells[o]?.[a]} onChange={(s) => setDraft({ ...draft, cells: setCell(draft.cells, o, a, s) })} />} />
            {!locked ? <div className="flex flex-wrap gap-2">
                <button type="button" onClick={save} disabled={saving} className={btnPrimary}>{saving ? "Сохранение…" : "Сохранить роль"}</button>
                {!draft.isNew ? <button type="button" onClick={archive} className={`${btn} text-red-600`}>Удалить роль</button> : null}
            </div> : null}
        </section> : <p className="text-sm text-slate-500">Выберите роль слева или создайте новую.</p>}
    </div>;
}

/* ---------- Люди ---------- */

function fmtDate(d) {
    if (!d) return "";
    const [y, m, day] = String(d).slice(0, 10).split("-");
    return day ? `${day}.${m}.${y}` : String(d);
}

/** Правка №34: «Доступ к адаптациям» — с кем поделились конкретной адаптацией и до какой даты. */
function AdaptationShares({ person, showAlert }) {
    const [shares, setShares] = useState([]);
    const [open, setOpen] = useState(null);
    const [form, setForm] = useState({ enrollment_id: "", until: "", comment: "" });
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            setShares(await getAdaptationShares(person.subject) || []);
        } catch (e) {
            showAlert(`Не удалось загрузить доступ к адаптациям: ${errText(e)}`, "error");
        }
    }, [person.subject, showAlert]);
    useEffect(() => { load(); }, [load]);

    const startAdding = async () => {
        try {
            setOpen(await getOpenAdaptations() || []);
        } catch (e) {
            showAlert(errText(e), "error");
        }
    };
    const run = async (fn, ok) => {
        setBusy(true);
        try {
            await fn();
            showAlert(ok, "success");
            await load();
        } catch (e) {
            showAlert(errText(e), "error");
        } finally {
            setBusy(false);
        }
    };
    const add = () => run(async () => {
        await addAdaptationShare({ subject: person.subject, enrollment_id: Number(form.enrollment_id), until: form.until, comment: form.comment });
        setForm({ enrollment_id: "", until: "", comment: "" });
        setOpen(null);
    }, "Доступ к адаптации открыт");

    const sharedIds = new Set(shares.map((s) => s.enrollment_id));
    return <section className="mt-6" data-testid="adaptation-shares">
        <div className="mb-2 flex items-center justify-between gap-3">
            <h3 className="font-bold text-slate-900">Доступ к адаптациям <span className="font-normal text-slate-500">({shares.length})</span></h3>
            {open === null ? <button type="button" className={btn} onClick={startAdding} data-testid="share-adaptation-start">Поделиться адаптацией</button> : null}
        </div>
        <p className="mb-2 text-xs text-slate-500">Руководитель видит адаптацию своих сотрудников, где он руководитель. Чужие — только если поделиться здесь
            или включить в роли «Адаптация: смотреть — отдела».</p>
        {shares.length ? <ul className="space-y-1 rounded-2xl border border-slate-200 bg-white p-3 text-sm">
            {shares.map((s) => <li key={s.id} className="flex flex-wrap items-center justify-between gap-2">
                <span className={s.active ? "" : "text-slate-400 line-through"}>
                    <span className="font-semibold">{s.employee_name}</span>
                    {s.start_date ? ` · с ${fmtDate(s.start_date)}` : ""}
                    {s.until ? ` · до ${fmtDate(s.until)}` : " · без срока"}
                    {s.comment ? ` · ${s.comment}` : ""}
                    {!s.active ? " (срок истёк)" : ""}
                </span>
                <button type="button" disabled={busy} className={`${btn} text-red-600`} data-testid={`share-remove-${s.id}`}
                    onClick={() => run(() => removeAdaptationShare(s.id), "Доступ снят")}>Снять</button>
            </li>)}
        </ul> : <p className="text-sm text-slate-500">Ни одной адаптацией не поделились.</p>}
        {open !== null ? <div className="mt-3 flex flex-wrap items-end gap-2 rounded-2xl border border-amber-200 bg-white p-3 text-sm">
            <label className="flex flex-col gap-1">Адаптация
                <select value={form.enrollment_id} onChange={(e) => setForm({ ...form, enrollment_id: e.target.value })}
                    className="rounded-lg border border-slate-200 px-2 py-1" data-testid="share-enrollment">
                    <option value="">— выберите —</option>
                    {open.filter((r) => !sharedIds.has(r.enrollment_id)).map((r) => <option key={r.enrollment_id} value={r.enrollment_id}>
                        {r.employee_name}{r.start_date ? ` · с ${fmtDate(r.start_date)}` : ""}{r.manager_name ? ` · рук. ${r.manager_name}` : ""}
                    </option>)}
                </select>
            </label>
            <label className="flex flex-col gap-1">До какой даты
                <input type="date" value={form.until} onChange={(e) => setForm({ ...form, until: e.target.value })}
                    className="rounded-lg border border-slate-200 px-2 py-1" data-testid="share-until" />
            </label>
            <label className="flex flex-col gap-1">Комментарий
                <input type="text" value={form.comment} maxLength={500} onChange={(e) => setForm({ ...form, comment: e.target.value })}
                    placeholder="например, замещает руководителя" className="w-64 rounded-lg border border-slate-200 px-2 py-1" />
            </label>
            <button type="button" disabled={busy || !form.enrollment_id} className={btnPrimary} onClick={add} data-testid="share-save">Поделиться</button>
            <button type="button" className={btn} onClick={() => setOpen(null)}>Отмена</button>
            <p className="w-full text-xs text-slate-500">Без даты — бессрочно. Действует, когда у человека включены «Роли и права».</p>
        </div> : null}
    </section>;
}

function PersonPanel({ person, matrix, roles, defaultRoleName, onClose, onChanged, showAlert }) {
    const [card, setCard] = useState(null);
    const [over, setOver] = useState({ allow: [], deny: [] });
    const [editOverrides, setEditOverrides] = useState(false);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const data = await getAccessSubject(person.subject);
            setCard(data);
            setOver({ allow: data.allow || [], deny: data.deny || [] });
        } catch (e) {
            showAlert(`Не удалось загрузить доступ: ${errText(e)}`, "error");
        }
    }, [person.subject, showAlert]);

    useEffect(() => { load(); }, [load]);

    const run = async (fn, ok) => {
        setBusy(true);
        try {
            await fn();
            if (ok) showAlert(ok, "success");
            await load();
            await onChanged();
        } catch (e) {
            showAlert(errText(e), "error");
        } finally {
            setBusy(false);
        }
    };

    const toggleRole = (key, on) => run(
        () => (on ? assignRole(person.subject, { role_key: key, user_name: person.full_name, department: person.department || null })
            : unassignRole(person.subject, key)),
        on ? "Роль назначена" : "Роль снята",
    );

    const roleCells = useMemo(() => {
        const keys = card?.roles || [];
        const grants = roles.filter((r) => keys.includes(r.key)).flatMap((r) => (r.system ? [] : r.grants || []));
        return grantsToCells(grants);
    }, [card, roles]);

    const summary = useMemo(() => describeGrants(card?.grants || [], matrix), [card, matrix]);

    return <div className="fixed inset-0 z-40 flex justify-end bg-black/30" onClick={onClose}>
        <div className="h-full w-full max-w-4xl overflow-y-auto bg-[#f5f5f5] p-5 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="person-panel">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <h2 className="text-2xl font-bold text-slate-900">{person.full_name}</h2>
                    <p className="text-sm text-slate-500">{[person.department, person.position].filter(Boolean).join(" · ") || "—"}</p>
                    <p className="mt-1 text-xs">{person.has_erp ? <span className="rounded bg-emerald-100 px-2 py-0.5 font-semibold text-emerald-800">есть ERP-аккаунт</span>
                        : <span className="rounded bg-slate-200 px-2 py-0.5 font-semibold text-slate-700">без ERP</span>}</p>
                </div>
                <button type="button" onClick={onClose} className={btn}>Закрыть</button>
            </div>
            {!person.has_erp ? <p className="mt-3 rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-900">
                Вход в платформу — только через ERP-аккаунт. Права сохраняются за сотрудником и начнут действовать,
                когда у него появится ERP-аккаунт.
            </p> : null}
            {card?.blocked ? <p className="mt-3 rounded-xl bg-red-50 px-3 py-2 text-sm font-semibold text-red-700">Доступ заблокирован — права не действуют.</p> : null}

            <section className="mt-5">
                <h3 className="mb-2 font-bold text-slate-900">Роли</h3>
                <div className="flex flex-wrap gap-2">
                    {roles.map((r) => {
                        const on = (card?.roles || []).includes(r.key);
                        return <label key={r.key} className={`flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-sm ${on ? "border-amber-400 bg-amber-50 font-semibold" : "border-slate-200 bg-white"}`}>
                            <input type="checkbox" checked={on} disabled={busy || !card} onChange={(e) => toggleRole(r.key, e.target.checked)} data-testid={`person-role-${r.key}`} />{r.name}
                        </label>;
                    })}
                </div>
                {card && !(card.roles || []).length ? <p className="mt-2 text-xs text-slate-500">Ролей не назначено — действует роль по умолчанию «{defaultRoleName}».</p> : null}
                <p className="mt-2 text-xs text-slate-500">Несколько ролей — права складываются.</p>
            </section>

            <section className="mt-6">
                <div className="mb-2 flex items-center justify-between gap-3">
                    <h3 className="font-bold text-slate-900">Личные исключения <span className="font-normal text-slate-500">({over.allow.length + over.deny.length})</span></h3>
                    <button type="button" onClick={() => setEditOverrides((v) => !v)} className={btn} data-testid="toggle-overrides">{editOverrides ? "Скрыть" : "Настроить"}</button>
                </div>
                {editOverrides ? <>
                    <p className="mb-2 text-xs text-slate-500">«Как в роли» — без изменений; «+» добавляет право; «Запрет» убирает действие, даже если оно есть в роли.</p>
                    <MatrixTable matrix={matrix} renderCell={(o, a) => {
                        const state = overrideState(o, a, over.allow, over.deny);
                        const fromRole = roleCells[o]?.[a];
                        return <select value={state} data-testid={`ov-${o}-${a}`} onChange={(e) => setOver(setOverride(over.allow, over.deny, o, a, e.target.value))}
                            className={`w-28 rounded-lg border px-1 py-1 text-xs ${state === "deny" ? "border-red-300 bg-red-50 text-red-700" : state.startsWith("allow") ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-slate-200 text-slate-500"}`}>
                            <option value="inherit">{fromRole ? `как в роли: ${SCOPE_LABELS[fromRole].toLowerCase()}` : "как в роли: —"}</option>
                            {SCOPE_ORDER.map((s) => <option key={s} value={`allow:${s}`}>+ {SCOPE_LABELS[s].toLowerCase()}</option>)}
                            <option value="deny">Запрет</option>
                        </select>;
                    }} />
                    <div className="mt-3"><button type="button" disabled={busy} className={btnPrimary} data-testid="save-overrides"
                        onClick={() => run(() => saveAccessOverrides(person.subject, { ...over, comment: null }), "Исключения сохранены")}>Сохранить исключения</button></div>
                </> : null}
            </section>

            <AdaptationShares person={person} showAlert={showAlert} />

            <section className="mt-6">
                <h3 className="mb-2 font-bold text-slate-900">Итог: что видит и может</h3>
                {summary.length ? <ul className="space-y-1 rounded-2xl border border-slate-200 bg-white p-3 text-sm">
                    {summary.map((s) => <li key={s.object}><span className="font-semibold">{s.label}:</span> {s.text}</li>)}
                </ul> : <p className="text-sm text-slate-500">Нет прав.</p>}
            </section>

            <section className="mt-6 flex flex-wrap gap-2">
                {card?.blocked
                    ? <button type="button" disabled={busy} className={btn} onClick={() => run(() => unblockAccess(person.subject), "Доступ разблокирован")}>Разблокировать</button>
                    : <button type="button" disabled={busy || !(card?.roles || []).length} className={`${btn} text-red-600`}
                        onClick={() => window.confirm(`Заблокировать доступ «${person.full_name}»?`) && run(() => blockAccess(person.subject), "Доступ заблокирован")}>Заблокировать доступ</button>}
                {person.employee_id ? (person.service_account
                    ? <button type="button" disabled={busy} className={btn} data-testid="service-account-show"
                        onClick={() => run(() => setServiceAccount(person.employee_id, { hidden: false }), "Возвращён в сотрудники")}>Вернуть в сотрудники</button>
                    : <button type="button" disabled={busy} className={btn} data-testid="service-account-hide"
                        onClick={() => window.confirm(`«${person.full_name}» — служебная учётка? Пропадёт из сотрудников, отделов, структуры и выгрузок.`)
                            && run(() => setServiceAccount(person.employee_id, { hidden: true, reason: "служебная учётка" }), "Скрыт как служебная учётка")}>Служебная учётка — скрыть</button>) : null}
            </section>
        </div>
    </div>;
}

function PeopleTab({ people, roles, matrix, defaultRole, reload, showAlert }) {
    const [query, setQuery] = useState("");
    const [showService, setShowService] = useState(false);
    const [erp, setErp] = useState("all");
    const [noRole, setNoRole] = useState(false);
    const [showFired, setShowFired] = useState(false);
    const [open, setOpen] = useState(null);
    const roleName = useMemo(() => Object.fromEntries(roles.map((r) => [r.key, r.name])), [roles]);
    const serviceCount = useMemo(() => people.filter((p) => p.service_account).length, [people]);
    const visible = useMemo(() => filterPeople(
        people.filter((p) => (showFired || !p.fired) && (showService ? p.service_account : !p.service_account)),
        { query, erp, noRole },
    ), [people, query, erp, noRole, showFired, showService]);
    const defaultRoleName = roleName[defaultRole] || defaultRole;

    return <div>
        <div className="mb-4 flex flex-wrap items-center gap-3">
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="ФИО, отдел, должность" data-testid="people-search"
                className="h-11 min-w-[240px] flex-1 rounded-xl border border-slate-200 px-3 text-sm" />
            <select value={erp} onChange={(e) => setErp(e.target.value)} className="h-11 rounded-xl border border-slate-200 px-3 text-sm" data-testid="people-erp">
                <option value="all">Все</option><option value="yes">С ERP</option><option value="no">Без ERP</option>
            </select>
            <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={noRole} onChange={(e) => setNoRole(e.target.checked)} />Без роли</label>
            <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={showFired} onChange={(e) => setShowFired(e.target.checked)} />Уволенные</label>
            <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={showService} onChange={(e) => setShowService(e.target.checked)} data-testid="people-service" />Служебные учётки ({serviceCount})</label>
        </div>
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
            <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                    <tr><th className="px-3 py-2">ФИО</th><th className="px-3 py-2">Отдел</th><th className="px-3 py-2">Должность</th><th className="px-3 py-2">ERP</th><th className="px-3 py-2">Роли</th><th className="px-3 py-2">Исключ.</th></tr>
                </thead>
                <tbody>
                    {visible.map((p) => <tr key={p.subject} onClick={() => setOpen(p)} className="cursor-pointer border-t border-slate-100 hover:bg-amber-50" data-testid="person-row">
                        <td className="px-3 py-2 font-semibold text-slate-900">{p.full_name}{p.blocked ? <span className="ml-2 text-xs font-bold text-red-600">заблокирован</span> : null}{p.fired ? <span className="ml-2 text-xs text-slate-400">уволен</span> : null}{p.service_account ? <span className="ml-2 text-xs text-slate-400">служебная</span> : null}</td>
                        <td className="px-3 py-2 text-slate-600">{p.department || "—"}</td>
                        <td className="px-3 py-2 text-slate-600">{p.position || "—"}</td>
                        <td className="px-3 py-2">{p.has_erp ? "есть" : <span className="text-slate-400">нет</span>}</td>
                        <td className="px-3 py-2">{(p.roles || []).length ? p.roles.map((r) => <span key={r} className="mr-1 inline-block rounded-lg bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">{roleName[r] || r}</span>) : <span className="text-xs text-slate-400">по умолчанию: {defaultRoleName}</span>}</td>
                        <td className="px-3 py-2 text-slate-600">{p.overrides || ""}</td>
                    </tr>)}
                    {!visible.length ? <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-500">Никого не найдено</td></tr> : null}
                </tbody>
            </table>
        </div>
        {open ? <PersonPanel person={open} matrix={matrix} roles={roles} defaultRoleName={defaultRoleName} onClose={() => setOpen(null)} onChanged={reload} showAlert={showAlert} /> : null}
    </div>;
}

/* ---------- Включение (флаги) ---------- */

function RolloutTab({ people, showAlert }) {
    const [flags, setFlags] = useState(null);
    const [drafts, setDrafts] = useState({});
    const erpPeople = useMemo(() => people.filter((p) => p.has_erp && p.erp_user_id), [people]);

    const load = useCallback(async () => {
        try {
            const data = await getFlags();
            setFlags(data);
            setDrafts(Object.fromEntries(data.map((f) => [f.key, { state: f.state, pilot_user_ids: f.pilot_user_ids || [] }])));
        } catch (e) {
            setFlags([]);
            showAlert(`Нет доступа к включению: ${errText(e)}`, "error");
        }
    }, [showAlert]);
    useEffect(() => { load(); }, [load]);

    const save = async (key) => {
        try {
            await saveFlag(key, drafts[key]);
            showAlert("Сохранено", "success");
            await load();
        } catch (e) {
            showAlert(errText(e), "error");
        }
    };
    if (!flags) return <p className="text-sm text-slate-500">Загрузка…</p>;
    const nameOf = (id) => erpPeople.find((p) => p.erp_user_id === id)?.full_name || id;

    return <div className="space-y-3">
        <p className="text-sm text-slate-600">Порядок включения: выключено → пилот на конкретных людях → для всех. «Роли и права» (admin_panel) — проверка прав по ролям этого экрана вместо роли ERP.</p>
        {flags.map((f) => {
            const d = drafts[f.key] || { state: f.state, pilot_user_ids: [] };
            return <div key={f.key} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`flag-${f.key}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div><div className="font-semibold text-slate-900">{f.key}</div><div className="text-xs text-slate-500">{f.description}</div></div>
                    <div className="flex items-center gap-2">
                        <select value={d.state} onChange={(e) => setDrafts({ ...drafts, [f.key]: { ...d, state: e.target.value } })} className="rounded-xl border border-slate-200 px-3 py-2 text-sm">
                            <option value="off">выключено</option><option value="pilot">пилот</option><option value="all">для всех</option>
                        </select>
                        <button type="button" className={btnPrimary} onClick={() => save(f.key)}>Сохранить</button>
                    </div>
                </div>
                {d.state === "pilot" ? <div className="mt-3 flex flex-wrap items-center gap-2">
                    {d.pilot_user_ids.map((id) => <span key={id} className="rounded-lg bg-amber-100 px-2 py-1 text-xs font-semibold">{nameOf(id)}
                        <button type="button" className="ml-1 text-amber-800" onClick={() => setDrafts({ ...drafts, [f.key]: { ...d, pilot_user_ids: d.pilot_user_ids.filter((x) => x !== id) } })}>×</button></span>)}
                    <select value="" onChange={(e) => e.target.value && setDrafts({ ...drafts, [f.key]: { ...d, pilot_user_ids: [...new Set([...d.pilot_user_ids, e.target.value])] } })} className="rounded-xl border border-slate-200 px-2 py-1 text-xs">
                        <option value="">+ добавить в пилот</option>
                        {erpPeople.map((p) => <option key={p.erp_user_id} value={p.erp_user_id}>{p.full_name}</option>)}
                    </select>
                </div> : null}
            </div>;
        })}
    </div>;
}

/* ---------- Журнал ---------- */

function LogTab({ showAlert }) {
    const [rows, setRows] = useState(null);
    useEffect(() => {
        getAccessLog(300).then(setRows).catch((e) => { setRows([]); showAlert(`Журнал недоступен: ${errText(e)}`, "error"); });
    }, [showAlert]);
    if (!rows) return <p className="text-sm text-slate-500">Загрузка…</p>;
    return <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500"><tr><th className="px-3 py-2">Когда</th><th className="px-3 py-2">Кто</th><th className="px-3 py-2">Что</th></tr></thead>
            <tbody>
                {rows.map((r) => <tr key={r.id} className="border-t border-slate-100">
                    <td className="whitespace-nowrap px-3 py-2 text-slate-500">{r.at ? new Date(r.at).toLocaleString("ru-RU") : "—"}</td>
                    <td className="px-3 py-2">{r.actor || "—"}</td>
                    <td className="px-3 py-2 text-slate-700">{r.details || r.action}</td>
                </tr>)}
                {!rows.length ? <tr><td colSpan={3} className="px-3 py-6 text-center text-slate-500">Изменений пока не было</td></tr> : null}
            </tbody>
        </table>
    </div>;
}

/* ---------- страница ---------- */

export default function EmployeeAccessPage() {
    const navigate = useNavigate();
    const { showAlert } = useAlertContext();
    const [tab, setTab] = useState("people");
    const [state, setState] = useState({ loading: true, error: null, me: null, matrix: null, roles: [], people: [] });

    const load = useCallback(async () => {
        try {
            const [me, rolesData, people] = await Promise.all([getAccessMe(), getRoles(), getAccessPeople()]);
            setState({ loading: false, error: null, me, matrix: rolesData.matrix, roles: rolesData.roles || [], people: people || [] });
        } catch (e) {
            setState((s) => ({ ...s, loading: false, error: errText(e) }));
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    return <MainLayout>
        <div className="mx-auto max-w-7xl space-y-5 p-4 md:p-6">
            <header className="rounded-3xl bg-slate-900 p-6 text-white">
                <button type="button" onClick={() => navigate("/employees")} className="text-sm text-white/70 hover:text-white">← Сотрудники</button>
                <h1 className="mt-2 text-3xl font-bold">Доступы</h1>
                <p className="mt-1 text-sm text-white/70">Кто что видит и может в платформе: роли, личные исключения, включение и журнал.</p>
            </header>
            <nav className="flex flex-wrap gap-2">
                {TABS.map((t) => <button key={t.key} type="button" onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
                    className={`rounded-xl px-4 py-2 text-sm font-semibold ${tab === t.key ? "bg-[#e0bb48] text-slate-900" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{t.label}</button>)}
            </nav>
            {state.loading ? <p className="text-sm text-slate-500">Загрузка…</p> : null}
            {state.error ? <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="access-error">
                Нет доступа к настройке прав или сервис недоступен: {state.error}</p> : null}
            {!state.loading && !state.error ? <>
                {tab === "people" ? <PeopleTab people={state.people} roles={state.roles} matrix={state.matrix} defaultRole={state.me?.default_role || "staff"} reload={load} showAlert={showAlert} /> : null}
                {tab === "roles" ? <>
                    <DefaultRole me={state.me} roles={state.roles} reload={load} showAlert={showAlert} />
                    <RolesTab matrix={state.matrix} roles={state.roles} reload={load} showAlert={showAlert} />
                </> : null}
                {tab === "rollout" ? <RolloutTab people={state.people} showAlert={showAlert} /> : null}
                {tab === "log" ? <LogTab showAlert={showAlert} /> : null}
            </> : null}
        </div>
    </MainLayout>;
}
